import { format } from "date-fns";
import React from "react";
import DataTableImport, { TableColumn } from "react-data-table-component";
import styled from "styled-components";
import { FlexColumn } from "../../styles";
import NoteCell from "./NoteCell";
import { interopDefault } from "../../interopDefault";

const DataTable = interopDefault(DataTableImport);

const customStyles = {
  tableWrapper: {
    style: {
      display: "block",
    },
  },

  headRow: {
    style: {
      display: "flex",
      alignItems: "center",
    },
  },
  headCells: {
    style: {
      fontFamily: "Inter",
      fontWeight: 400,
      fontSize: "1.1rem",
      fontStyle: "normal",
      letterSpacing: "-0.07em",
      color: "#201f1f",
    },
  },
  rows: {
    style: {
      fontFamily: "Inter",
      fontWeight: 400,
      fontSize: "14px",
      letterSpacing: "-0.07em",
      color: "rgba(90, 90, 90, 0.8)",
      borderBottom: "1px solid #E3E9F0",
    },
  },
};

type Props = {
  data: Array<{
    projectName: string;
    createdAt: number;
    unit: string;
    totalInvested: number;
    note?: string;
  }>;
};

const StyledWrapper = styled(FlexColumn)`
  padding-left: 96px;
  margin-bottom: 32px;
  padding-right: 96px;
  border-radius: 16px;
  justify-content: center;
`;

const formatInvestment = (value: number): string => {
  if (value < 0.0001) {
    return "";
  }
  return value.toFixed(4).replace(/\.?0+$/, "");
};

const unitLabels: { [unit: string]: { singular: string; plural: string } } = {
  Biodiversity: {
    singular: "Biodiversity credit",
    plural: "Biodiversity credits",
  },
  "Minutes of monitoring footage": {
    singular: "minute of monitoring footage",
    plural: "minutes of monitoring footage",
  },
  "Monitoring images": {
    singular: "monitoring image",
    plural: "monitoring images",
  },
  "Monitoring stations": {
    singular: "monitoring station",
    plural: "monitoring stations",
  },
};

export const formatUnit = (unit: string, value: number): string => {
  if (!unit || typeof value !== "number") return "";
  const labels = unitLabels[unit];
  if (labels) {
    return value > 1 ? labels.plural : labels.singular;
  }
  return unit;
};

function TransactionLedgerTable({ data }: Props) {
  const sortedData = data.sort((a, b) => b.createdAt - a.createdAt);

  const columns: TableColumn<{
    projectName: string;
    createdAt: number;
    unit: string;
    totalInvested: number;
    note?: string;
  }>[] = [
    {
      name: "Project",
      selector: (row) => row.projectName,
      maxWidth: "45%",
    },
    {
      name: "Date",
      selector: (row) => row.createdAt,
      sortable: true,
      cell: (row) => format(new Date(row.createdAt), "MMM d, yyyy"),
      maxWidth: "15%",
    },
    {
      name: "Investment",
      selector: (row) => {
        const formattedInvestment = formatInvestment(row.totalInvested);
        if (formattedInvestment === "") {
          return "";
        }
        const formattedUnit = formatUnit(row.unit, row.totalInvested);
        return `${formattedInvestment} ${formattedUnit}`;
      },
      maxWidth: "15%",
    },
    {
      name: "Note",
      wrap: true,
      cell: NoteCell,
    },
  ];

  return (
    <StyledWrapper>
      <DataTable
        columns={columns}
        data={sortedData}
        pagination={sortedData.length > 10}
        paginationPerPage={10}
        customStyles={customStyles}
      />
    </StyledWrapper>
  );
}

export default TransactionLedgerTable;
